import {
	IApiEndpoint,
	IObjectCounter,
	IOperationResponse,
	IPagination,
} from "@localization/siteMap/siteMapInterfaces.ts";
import type {IAddRecursoMembroDto} from "@models/recurso/addRecursoMembroDto.ts";
import {type INovaRecursoDto, type IRecursoFiltroDto} from "@models/recurso/dto.ts";
import {IRecursoParam} from '@models/recurso/IRecursoParam.ts';
import type {IRecursoTransferenciaSolicitacao} from "@models/recurso/IRecursoTransferencia.ts";
import {IRecurso} from "@models/recurso/IRecurso.ts";
import {siteMapApi} from "@localization/siteMap/siteMap.ts";
import {RecursoLogFilterDto} from '@models/recurso/RecursoLogFilterDto.tsx';
import {IRecursoMembroGetDto} from "@models/recurso/RecursoMembroDto.ts";
import {IRecursoMembro} from "@models/recurso/IRecursoMembro.ts";
import type {
	IRecursoTransferenciaDto,
	IRecursoTransferenciaSolicitacaoAprovacaoDto,
	IRecursoTransferenciaSolicitacaoDto,
	IRecursoTransferenciaSolicitacaoFiltroDto,
} from "@models/recurso/RecursoTransferenciaDto.ts";
import {IRecursoMovimentacao} from "@models/recurso/IRecursoMovimentacao.ts";
import {RecursoMovimentacaoFilterDto} from "@models/recurso/RecursoMovimentacaoDto.tsx";
import {ILog} from '@models/seguranca/ILog.ts';
import {IFuncaoRecurso} from "@models/conta/funcaoRecurso.ts";
import {RecursosMovimentacoesFilters} from "@models/recurso/movimentacoes/RecursosMovimentacoesFilters.ts";
import {IDataNoPageDto} from "@models/shared/IDataDto.ts";
import {ICreateRecursoDto} from "@components/recurso/models/dto/ICreateRecursoDto.ts";

export const _RecursosEndpoints = {
    GetRecurso: (filters: IRecursoFiltroDto, pagination: IPagination): IApiEndpoint<IRecurso[]> => ({
        url: '/api/recurso/GetRecursos',
        pagination: pagination,
        query: filters,
        method: "GET",
    }),
    getRecursoPesquisa: (search: string, pagination: IPagination): IApiEndpoint => ({
        url: '/api/recurso/GetRecursoPesquisa',
        pagination: pagination,
        query: {
            search: search,
        },
        method: "GET",
    }),
    getRecursoMembros: (filters: IRecursoMembroGetDto, pagination: IPagination): IApiEndpoint => ({
        url: '/api/recurso/GetRecursoMembros',
        pagination: pagination,
        query: filters,
        method: "GET",
    }),
    addRecursoMembro: (body: IAddRecursoMembroDto[]): IApiEndpoint<IRecursoMembro[]> => ({
        url: '/api/recurso/AddRecursoMembro',
        body: body,
        method: 'POST',
    }),
    tranferenciaEntreRecursos: (body: IRecursoTransferenciaDto): IApiEndpoint => ({
        url: '/api/recurso/TransferirEntreRecursos',
        body: body,
        method: 'PUT',
    }),
    CreateSolicitacaoTransferenciaRecurso: (body: IRecursoTransferenciaSolicitacaoDto): IApiEndpoint<IRecursoTransferenciaSolicitacao> => ({
        url: '/api/recurso/CreateSolicitacaoTransferenciaRecurso',
        body: body,
        method: 'POST',
    }),
    GetSolicitacaoTransferenciaRecurso: (filters: IRecursoTransferenciaSolicitacaoFiltroDto, pagination: IPagination): IApiEndpoint<IRecursoTransferenciaSolicitacao[]> => ({
        url: '/api/recurso/GetSolicitacaoTransferenciaRecurso',
        pagination: pagination,
        query: filters,
        method: "GET",
    }),
    aprovarOuRejeitarSolicitacaoTransferenciaRecurso: (body: IRecursoTransferenciaSolicitacaoAprovacaoDto): IApiEndpoint => ({
        url: '/api/recurso/AprovarOuRejeitarSolicitacaoTransferenciaRecurso',
        body: body,
        method: 'PUT',
    }),
    transferirAPartirDeSolicitacao: (idSolicitacao: number): IApiEndpoint => ({
        url: '/api/recurso/TransferirAPartirDeSolicitacao',
        query: {idSolicitacao},
        method: 'PUT',
    }),
    createRecurso: (body: INovaRecursoDto, acaoConfirmada: boolean): IApiEndpoint => ({
        url: '/api/recurso/CreateRecurso',
        body: body,
        query: {acaoConfirmada},
        method: 'POST',
    }),
    GetMinhasPermissoesRecurso: (recursoId: number): IApiEndpoint<IFuncaoRecurso[]> => ({
        url: '/api/recurso/GetMinhasPermissoesRecurso',
        query: {recursoId},
        method: 'GET',
    }),
    UpdateRecurso: (body: IRecurso): IApiEndpoint<IRecurso> => ({
        url: '/api/recurso/UpdateRecurso',
        body: body,
        method: 'PATCH',
    }),
    removeRecursoMembro: (body: IAddRecursoMembroDto[]): IApiEndpoint<IRecursoMembro[]> => ({
        url: '/api/recurso/RemoveRecursoMembro',
        body: body,
        method: 'DELETE',
    }),
    /**
     * @deprecated usar GetRecursosMovimentacoes
     * @param filters
     * @param pagination
     */
    getRecursoMovimentacaoes: (filters: RecursoMovimentacaoFilterDto, pagination: IPagination): IApiEndpoint<IRecursoMovimentacao[], IObjectCounter[]> => ({
        url: '/api/recurso/GetMovimentacoesRecurso',
        pagination: pagination,
        query: filters,
        method: "GET",
    }),
    createMovimentacaoRecurso: (body: IRecursoMovimentacao): IApiEndpoint<IRecursoMovimentacao> => ({
        url: '/api/recurso/CreateMovimentacaoRecurso',
        body: body,
        method: 'POST',
    }),
    GetRecursoParams: ({recursoId}: {
        recursoId?: number
    }, pagination: IPagination): IApiEndpoint<IRecursoParam[]> => ({
        url: `/api/recurso/GetRecursoParams${recursoId ? `/${recursoId}` : ''}`,
        pagination: pagination,
        method: "GET",
    }),
    GetRecursosMovimentacoes: (filters: RecursosMovimentacoesFilters, pagination: IPagination): IApiEndpoint<IRecursoMovimentacao[]> => ({
        url: '/api/Recurso/GetMovimentacoesRecursos',
        query: filters,
        pagination,
        method: "GET"
    }),
    CancelarMovimentacaoRecurso: (id: number): IApiEndpoint<IRecursoMovimentacao> => ({
        url: `/api/recurso/CancelarMovimentacaoRecurso/${id}`,
        method: 'POST',
    }),
    FinalizarMovimentacaoRecurso: (id: number): IApiEndpoint<IRecursoMovimentacao> => ({
        url: `/api/recurso/FinalizarMovimentacaoRecurso/${id}`,
        method: 'POST',
    }),
    ImportarRecursos: (data: IDataNoPageDto<ICreateRecursoDto[]>): IApiEndpoint => ({
        url: '/api/recurso/ImportarRecursos',
        data: data,
        method: 'POST',
    })
};


export const GetRecursoPesquisa = (search: string, pagination: IPagination): IApiEndpoint<IRecurso[], IObjectCounter[]> => {
    return {
        url: '/api/recurso/GetRecursoPesquisa',
        pagination: pagination,
        query: {
            search: search,
        },
        method: "GET",
    };
};

export const CreateRecurso = async (dados: INovaRecursoDto, acaoConfirmada: boolean): Promise<IOperationResponse<IRecurso>> => {
    const endpoint = _RecursosEndpoints.createRecurso(dados, acaoConfirmada);

    return await siteMapApi.request<IRecurso>(endpoint);
};

export const GetRecursoMembros = async (dados: IRecursoMembroGetDto, pagination: IPagination): Promise<IOperationResponse<IRecursoMembro[]>> => {
    const endpoint = _RecursosEndpoints.getRecursoMembros(dados, pagination);

    return await siteMapApi.fetch<IOperationResponse<IRecursoMembro[]>>(endpoint);
};



export const GetSolicitacaoTransferenciaRecurso = async (dados: IRecursoTransferenciaSolicitacaoFiltroDto, pagination: IPagination): Promise<IOperationResponse<IRecursoTransferenciaSolicitacao[]>> => {
    const endpoint = _RecursosEndpoints.GetSolicitacaoTransferenciaRecurso(dados, pagination);

    return await siteMapApi.fetch<IOperationResponse<IRecursoTransferenciaSolicitacao[]>>(endpoint);
};

export const UpdateRecurso = (recurso: IRecurso) => {
    return _RecursosEndpoints.UpdateRecurso(recurso);
};

export const AddRecursoMembro = (recursoMembro: IAddRecursoMembroDto[]) => {
    return _RecursosEndpoints.addRecursoMembro(recursoMembro);
};

export const RemoveRecursoMembro = (recursoMembro: IAddRecursoMembroDto[]) => {
    return _RecursosEndpoints.removeRecursoMembro(recursoMembro);
};

export const GetRecursoMovimentacoes = (filters: RecursoMovimentacaoFilterDto, pagination: IPagination) => {
    return _RecursosEndpoints.getRecursoMovimentacaoes(filters, pagination);
};

export const CreateMovimentacaoRecurso = (movimentacao: IRecursoMovimentacao) => {
    return _RecursosEndpoints.createMovimentacaoRecurso(movimentacao);
};

export const GetLimitesRecurso = (dto: {
    contaContabilId: number
    recursoId?: number
    search?: string
}, pagination: IPagination): IApiEndpoint<IRecurso[]> => {
    return {
        url: '/api/recurso/GetLimitesRecursoContaContabil',
        pagination: pagination,
        query: dto,
        method: "GET",
    };
};


export const GetRecursoLogs = (filter: RecursoLogFilterDto, pagination: IPagination): IApiEndpoint<ILog[]> => {
    return {
        url: '/api/Recurso/GetLogRecurso',
        pagination: pagination,
        query: filter,
        method: "GET",
    };
};